import { buildGroundingReferenceBlocks, GROUNDING_REFERENCE_MARKER } from "@/utility/groundingMarkers";
import { applyDraftStyleToRange, isTemplateHeadingParagraph } from "@/utility/wordStyleAdoption";

export const REPORT_DOWNLOAD_ADOPTION_VERSION = 2;
export const REPORT_DOWNLOAD_ADOPTION_PROPERTY = "jenlyAdoptionState";

/**
 * Calibrated work units representing relative Word JS execution costs across
 * stages. Section styling and marker linking require heavier DOM traversals
 * and ContentControl wraps compared to simple bookmark lookups.
 */
export const ADOPTION_WEIGHTS = {
  INSPECT: 1,
  SECTION_CHECK: 1,
  MARKER_LINK: 3,
  SECTION_STYLE: 8,
  VERIFY: 2,
  SAVE: 2,
} as const;

export type AdoptionStage =
  | "inspect"
  | "render"
  | "sections"
  | "markers"
  | "styling"
  | "verify"
  | "save";

export interface AdoptionProgress {
  stage: AdoptionStage;
  /** Human-readable stage name for the pane. */
  label: string;
  processed: number;
  total: number;
  /**
   * Section-level view for the restore page. `stage`/`processed`/`total`
   * above stay in STAGE UNITS (markers for the markers stage, sections for
   * the sections stage) so the "completed units, not a timer" contract is
   * unchanged; the fields below are the section-level projection the page
   * renders its percentage and per-section list from.
   */
  sectionsTotal: number;
  /** Sections whose every marker is linked. Drives the section list. */
  sectionsDone: number;
  /**
   * Indices of the sections counted in `sectionsDone`. Exposed explicitly
   * because the count alone is not enough to render the list: a section with
   * no citations is credited up front, so completed sections are NOT
   * necessarily a prefix of the document order.
   */
  sectionsComplete: number[];
  sectionsPreserved?: number[];
  /**
   * Cumulative discrete operations completed across EVERY stage — this, not
   * sectionsDone, is what the restore page's percentage reads. Linking alone
   * finishes long before styling, verification and saving are done, so a
   * section-based bar would sit at 100% through the rest of the work.
   */
  unitsDone: number;
  /**
   * Weighted work units across all adoption stages: inspect(1), section checks(1*S),
   * marker links(3*M), section styling(8*S), verification(2), and save(2).
   */
  unitsTotal: number;
  /** Section currently being worked, or null outside the section stages. */
  sectionIndex: number | null;
  sectionName: string | null;
  sectionMarkersDone: number;
  sectionMarkersTotal: number;
}

export type AdoptionProgressReporter = (progress: AdoptionProgress) => void;

export interface AdoptionMarker {
  bookmark: string;
  tag: string;
  title: string;
  sectionName: string;
  /**
   * Position of the owning section in the `sections` array the caller passed.
   * `buildDownloadAdoptionMarkers` flat-maps in section order, so this is
   * an exact attribution — display names can repeat, so the page must never
   * match sections by name.
   */
  sectionIndex: number;
}

const STAGE_LABELS: Record<AdoptionStage, string> = {
  inspect: "Reading your report…",
  render: "Rebuilding each section…",
  sections: "Finding each section…",
  markers: "Re-linking citations to their sources…",
  styling: "Matching Jenly's generated look…",
  verify: "Making sure Word kept every link…",
  save: "Finishing up…",
};

export const adoptionStageLabel = (stage: AdoptionStage): string => STAGE_LABELS[stage];

export const buildDownloadAdoptionMarkers = (sections: any[]): AdoptionMarker[] =>
  sections.flatMap((section, sectionIndex) => {
    const taskId = section.agent_task_id;
    const markdown = section.grounding?.markdown || section.content;
    if (!taskId || !section.grounding || !markdown) return [];
    return buildGroundingReferenceBlocks(taskId, markdown).map((block, index) => ({
      bookmark: `JREF_${Number(section.order) + 1}_${index + 1}`,
      // The ordinal makes repeated citations individually identifiable. The
      // existing tag parser accepts the suffix and retains all grounding IDs.
      tag: `${block.tag}:a${index + 1}`,
      title: block.sourceOnly ? "Sources" : `Rationale ${block.id.replace(/^\D+/, "")}`,
      sectionName: section.display_name || section.insertion_key,
      sectionIndex,
    }));
  });

/**
 * One section's row on the restore page, derived purely from the markers so
 * the page never has to re-derive marker counts or guess section identity.
 */
export interface AdoptionSectionProgress {
  index: number;
  key: string;
  name: string;
  markersTotal: number;
  markersDone: number;
  /** True once every marker of this section is linked. */
  complete: boolean;
}

export interface AdoptionSectionTotals {
  sections: AdoptionSectionProgress[];
  sectionsTotal: number;
  /** Sections with no citations to link: nothing to adopt, so already done. */
  sectionsPreCredited: number;
}

export const buildAdoptionSectionTotals = (sections: any[], markers: AdoptionMarker[]): AdoptionSectionTotals => {
  const byIndex = new Map<number, number>();
  for (const marker of markers) byIndex.set(marker.sectionIndex, (byIndex.get(marker.sectionIndex) || 0) + 1);
  const rows = sections.map((section, index) => ({
    index,
    key: String(section.insertion_key || ""),
    name: section.display_name || section.insertion_key || `Section ${index + 1}`,
    markersTotal: byIndex.get(index) || 0,
    markersDone: 0,
    complete: false,
  }));
  // A section with zero markers (ungrounded, or no [^S]/[^R] references) has
  // nothing to link. It must count as done from the start or the percentage
  // could never reach 100.
  const sectionsPreCredited = rows.filter((row) => row.markersTotal === 0).length;
  return { sections: rows, sectionsTotal: rows.length, sectionsPreCredited };
};

/**
 * Per-section rows for the restore page's list, with real marker totals. Home
 * computes these once and hands them to the page so the page never re-derives
 * marker counts from a second marker build.
 */
export const buildAdoptionSectionRows = (sections: any[]): AdoptionSectionProgress[] =>
  buildAdoptionSectionTotals(sections, buildDownloadAdoptionMarkers(sections)).sections;

/**
 * Whether the recorded adoption state matches this download and pass. Says
 * nothing about whether Word kept the controls — callers AND that with
 * `allPresent`, because Word may drop or duplicate a control after the fact.
 * Shared by `probeReportDownloadAdoption` (read-only) and `adoptReportDownload`
 * (defence in depth) so the two can never disagree and strand the user on a
 * restore page with no work left to do.
 */
const isAdoptionStateCurrent = (downloadId: string, markerCount: number, rawState: unknown): boolean => {
  if (rawState === undefined || rawState === null || rawState === "") return false;
  let state: any;
  try {
    state = JSON.parse(String(rawState));
  } catch (_) {
    return false;
  }
  return state.v === REPORT_DOWNLOAD_ADOPTION_VERSION && state.id === downloadId && state.markers === markerCount;
};

/**
 * Read-only check for whether a downloaded report still needs the adoption
 * pass. Loads custom properties and content controls and writes nothing, so
 * Home can skip the restore page entirely for an already-adopted document
 * instead of flashing a 100% screen on every reopen.
 */
export async function probeReportDownloadAdoption(downloadId: string, sections: any[]): Promise<boolean> {
  const markers = buildDownloadAdoptionMarkers(sections);
  return Word.run(async (context) => {
    const state = context.document.properties.customProperties.getItemOrNullObject(
      REPORT_DOWNLOAD_ADOPTION_PROPERTY,
    );
    state.load("isNullObject,value");
    const controls = context.document.contentControls;
    controls.load("items/tag");
    await context.sync();
    const counts = new Map<string, number>();
    for (const control of controls.items) counts.set(control.tag, (counts.get(control.tag) || 0) + 1);
    if (state.isNullObject) return false;
    const allPresent = markers.every((marker) => counts.get(marker.tag) === 1);
    return isAdoptionStateCurrent(downloadId, markers.length, state.value) && allPresent;
  });
}

/**
 * Link an assembled DOCX to its frozen grounding through Word's own API.
 *
 * Progress is reported from COMPLETED work only (one report per verified
 * unit) — never from a timer. Every stage failure names its stage so the
 * pane can offer a safe retry; the function is idempotent, so retrying an
 * interrupted pass finishes exactly what is missing.
 */
export async function adoptReportDownload(
  downloadId: string,
  sections: any[],
  onProgress?: AdoptionProgressReporter,
  options: { applyStyling?: boolean; saveState?: boolean; appearance?: Word.ContentControlAppearance } = {}
): Promise<boolean> {
  const markers = buildDownloadAdoptionMarkers(sections);
  const totals = buildAdoptionSectionTotals(sections, markers);
  // Sections with nothing to link are done before any Word work happens, so
  // the restore page can never stall below 100% waiting on a marker that will
  // never exist.
  const rows = totals.sections.map((row) => ({ ...row, complete: row.markersTotal === 0 }));
  let sectionsDone = rows.filter((row) => row.complete).length;
  let activeIndex: number | null = null;
  let activeName: string | null = null;
  let activeDone = 0;
  let activeTotal = 0;

  // Grand total across the WHOLE pass, so the restore page's bar tracks all
  // of it rather than pinning itself to 100% once linking is done. See
  // AdoptionProgress.unitsTotal.
  const sectionCount = sections.length;
  const markerCount = markers.length;
  const stylingWeight = options.applyStyling === false ? 0 : ADOPTION_WEIGHTS.SECTION_STYLE;
  const unitsTotal =
    ADOPTION_WEIGHTS.INSPECT +
    sectionCount * ADOPTION_WEIGHTS.SECTION_CHECK +
    markerCount * ADOPTION_WEIGHTS.MARKER_LINK +
    sectionCount * stylingWeight +
    ADOPTION_WEIGHTS.VERIFY +
    ADOPTION_WEIGHTS.SAVE;
  let unitsDone = 0;

  const report = (stage: AdoptionStage, processed: number, total: number, units = unitsDone) => {
    unitsDone = units;
    onProgress?.({
      stage,
      label: STAGE_LABELS[stage],
      processed,
      total,
      sectionsTotal: totals.sectionsTotal,
      sectionsDone,
      sectionsComplete: rows.filter((row) => row.complete).map((row) => row.index),
      unitsDone,
      unitsTotal,
      sectionIndex: activeIndex,
      sectionName: activeName,
      sectionMarkersDone: activeDone,
      sectionMarkersTotal: activeTotal,
    });
  };

  /** Point the section-level view at one section for the reports that follow. */
  const focusSection = (index: number | null) => {
    if (index === null) {
      activeIndex = null;
      activeName = null;
      activeDone = 0;
      activeTotal = 0;
      return;
    }
    const row = rows[index];
    activeIndex = index;
    activeName = row ? row.name : null;
    activeDone = row ? row.markersDone : 0;
    activeTotal = row ? row.markersTotal : 0;
  };

  return Word.run(async (context) => {
    const properties = context.document.properties.customProperties;
    const state = properties.getItemOrNullObject(REPORT_DOWNLOAD_ADOPTION_PROPERTY);
    state.load("isNullObject,value");
    const controls = context.document.contentControls;
    controls.load("items/tag");
    await context.sync();
    report("inspect", 1, 1, ADOPTION_WEIGHTS.INSPECT);

    const counts = new Map<string, number>();
    for (const control of controls.items) counts.set(control.tag, (counts.get(control.tag) || 0) + 1);
    const allPresent = markers.every((marker) => counts.get(marker.tag) === 1);
    const completed = !state.isNullObject && isAdoptionStateCurrent(downloadId, markers.length, state.value);

    let checked = 0;
    for (const section of sections) {
      const key = String(section.insertion_key || "");
      const start = context.document.getBookmarkRangeOrNullObject(`${key}_START`);
      const end = context.document.getBookmarkRangeOrNullObject(`${key}_END`);
      start.load("isNullObject");
      end.load("isNullObject");
      await context.sync();
      if (start.isNullObject || end.isNullObject) {
        throw new Error(`Section bookmarks are missing for ${section.display_name || key}.`);
      }
      checked += 1;
      report("sections", checked, sections.length, ADOPTION_WEIGHTS.INSPECT + checked * ADOPTION_WEIGHTS.SECTION_CHECK);
    }
    if (completed && allPresent) {
      // Every marker is already linked: nothing for the restore page to show.
      focusSection(null);
      report("verify", markers.length, markers.length, unitsTotal);
      return false;
    }

    let linked = 0;
    for (const marker of markers) {
      const row = rows[marker.sectionIndex];
      focusSection(row ? row.index : null);
      const count = counts.get(marker.tag) || 0;
      if (count > 1) throw new Error(`Grounding marker ${marker.bookmark} has duplicate controls.`);
      if (count === 1) {
        linked += 1;
        if (row && !row.complete) {
          row.markersDone += 1;
          activeDone = row.markersDone;
          if (row.markersDone === row.markersTotal) {
            row.complete = true;
            sectionsDone += 1;
          }
        }
        report(
          "markers",
          linked,
          markers.length,
          ADOPTION_WEIGHTS.INSPECT + sectionCount * ADOPTION_WEIGHTS.SECTION_CHECK + linked * ADOPTION_WEIGHTS.MARKER_LINK,
        );
        continue;
      }
      const range = context.document.getBookmarkRangeOrNullObject(marker.bookmark);
      range.load("isNullObject,text");
      await context.sync();
      const label = String(range.text || "").trim();
      if (range.isNullObject || label !== GROUNDING_REFERENCE_MARKER) {
        // Older deployed assemblers labelled each diamond with its citation
        // IDs ("✦ (R1, S1)") or square brackets hugging it ("[✦]")
        // before the bare-glyph contract landed. Heal
        // those in place by replacing the labelled span with the bare glyph,
        // so the document ends up byte-identical to a freshly generated one
        // (no brackets, exactly one visible character inside the control).
        const legacy = /^\s*[[(]?\s*\u2726(?:\s*(?:\(([^)]*)\)|\[([^\]]*)\]))?\s*[\])]?\s*$/.exec(label);
        if (range.isNullObject || !legacy) {
          throw new Error(`Grounding marker ${marker.bookmark} in ${marker.sectionName} has changed or is missing.`);
        }
        range.insertText(GROUNDING_REFERENCE_MARKER, "Replace");
        await context.sync();
      }
      const parent = range.parentContentControlOrNullObject;
      parent.load("isNullObject,tag,text");
      await context.sync();
      if (!parent.isNullObject && (parent.tag || parent.text.trim() !== GROUNDING_REFERENCE_MARKER)) {
        throw new Error(`Grounding marker ${marker.bookmark} in ${marker.sectionName} is already inside another control.`);
      }
      range.font.underline = Word.UnderlineType.none;
      // A prior Office.js pass can stop after inserting a control but before
      // its tag is saved. Finish that control when it wraps only this glyph.
      const control = parent.isNullObject ? range.insertContentControl() : parent;
      control.tag = marker.tag;
      control.title = marker.title;
      // Parity with the generation path (wordInsertion.applyGroundingMarkerContentControls),
      // which pairs `boundingBox` with the draft font style. The visible box is
      // the "pending review" indicator, not a rendering artifact: acceptChanges
      // flips it to `hidden` and applies the confirmed style together, when the
      // user reviews the section. Adopted sections arrive unreviewed — they get
      // draft styling below and start with outputDecisionStatus true — so they
      // must start with the box too.
      //
      // This previously used `hidden` by mistake: the old comment cited
      // useWordDocument.hideGroundingMarkersInRange, which is the REVIEW path,
      // not the generation path. As a result the box never appeared for a
      // downloaded report at all, and acceptChanges only re-hides controls, so
      // there was no way to get it back.
      //
      // Click handling is unaffected either way: MainPage's onEntered and
      // onSelectionChanged handlers match by tag, not appearance.
      control.appearance = options.appearance || Word.ContentControlAppearance.boundingBox;
      control.cannotEdit = false;
      control.cannotDelete = false;
      await context.sync();
      linked += 1;
      if (row && !row.complete) {
        row.markersDone += 1;
        activeDone = row.markersDone;
        if (row.markersDone === row.markersTotal) {
          row.complete = true;
          sectionsDone += 1;
        }
      }
      report(
        "markers",
        linked,
        markers.length,
        ADOPTION_WEIGHTS.INSPECT + sectionCount * ADOPTION_WEIGHTS.SECTION_CHECK + linked * ADOPTION_WEIGHTS.MARKER_LINK,
      );
    }

    // Give adopted sections the add-in's draft (AI-generated) look — green
    // + italic — so a downloaded report is indistinguishable from output the
    // user just generated here. Sections start life as draft text in the
    // generation path; only an explicit in-Word review flips them to the
    // confirmed style, and that must remain a user decision. Locally
    // rejected sections never reach this loop (filtered by the caller).
    //
    // Styling never advances `sectionsDone`: the percentage tracks linking,
    // which is the work that actually adopts the report. Styling is cosmetic
    // and log-and-swallowed below, so it must not be able to gate progress.
    try {
      let styled = 0;
      for (let index = 0; options.applyStyling !== false && index < sections.length; index += 1) {
        const section = sections[index];
        focusSection(index);
        const key = String(section.insertion_key || "");
        const start = context.document.getBookmarkRangeOrNullObject(`${key}_START`);
        const end = context.document.getBookmarkRangeOrNullObject(`${key}_END`);
        start.load("isNullObject");
        end.load("isNullObject");
        await context.sync();
        if (start.isNullObject || end.isNullObject) continue;
        const body = start.expandTo(end);
        let nonHeadingParas: Word.Paragraph[] = [];

        try {
          const paras = body.paragraphs;
          if (paras) {
            paras.load("items/text, items/style, items/styleBuiltIn, items/font");
            await context.sync();
            if (paras.items && paras.items.length > 0) {
              for (let pIdx = 0; pIdx < paras.items.length; pIdx++) {
                const p = paras.items[pIdx];
                if (!isTemplateHeadingParagraph(p, pIdx, paras.items.length)) {
                  nonHeadingParas.push(p);
                }
              }
            }
          }
        } catch (_) {
          // paragraphs collection load fallback
        }

        if (nonHeadingParas.length > 0) {
          for (const p of nonHeadingParas) {
            await applyDraftStyleToRange(p.getRange());
          }
        } else if (!body.paragraphs) {
          // Fallback if paragraphs collection is not available (e.g. mock test harnesses)
          await applyDraftStyleToRange(body);
        }

        try {
          const targetParas = nonHeadingParas.length > 0 ? nonHeadingParas : (body.paragraphs?.items || []);
          for (const p of targetParas) {
            const runs = (p as any).runs;
            if (runs) {
              runs.load("items/font/color, items/font/highlightColor, items/font/size, items/font/name");
            }
          }
          await context.sync();
          for (const p of targetParas) {
            const runs = (p as any).runs;
            if (runs && runs.items) {
              for (const r of runs.items) {
                if (r.font) {
                  if (r.font.highlightColor) {
                    const hl = String(r.font.highlightColor).toLowerCase();
                    if (hl === "yellow" || hl === "#ffff00") r.font.highlightColor = null;
                  }
                  if (r.font.color) {
                    const col = String(r.font.color).toUpperCase().replace("#", "");
                    if (["D9D9D9", "595959", "7F7F7F", "808080", "A6A6A6", "E7E6E6"].includes(col)) {
                      r.font.color = "#1F6B3E";
                    }
                  }
                  if (r.font.size && r.font.size > 10 && r.font.size <= 12) {
                    r.font.size = 10;
                  }
                }
              }
            }
          }
          await context.sync();
        } catch (_) {
          // best-effort cleanup
        }

        styled += 1;
        report(
          "styling",
          markers.length,
          markers.length,
          ADOPTION_WEIGHTS.INSPECT +
            sectionCount * ADOPTION_WEIGHTS.SECTION_CHECK +
            markerCount * ADOPTION_WEIGHTS.MARKER_LINK +
            styled * stylingWeight,
        );
      }
    } catch (styleError) {
      // Styling is cosmetic: a failure must never block adoption or trigger
      // a retry loop. Log-and-continue keeps the marker links authoritative.
      console.warn("adoptReportDownload: draft styling could not be applied", styleError);
    }

    const verifiedControls = context.document.contentControls;
    verifiedControls.load("items/tag");
    await context.sync();
    const verified = new Map<string, number>();
    for (const control of verifiedControls.items) {
      verified.set(control.tag, (verified.get(control.tag) || 0) + 1);
    }
    if (!markers.every((marker) => verified.get(marker.tag) === 1)) {
      throw new Error("Word did not retain every grounding control in the downloaded report.");
    }
    // Verify and save are document-wide, not per-section, so no section is
    // "current" for them — the page reads this window as its final phase.
    focusSection(null);
    report(
      "verify",
      markers.length,
      markers.length,
      ADOPTION_WEIGHTS.INSPECT +
        sectionCount * ADOPTION_WEIGHTS.SECTION_CHECK +
        markerCount * ADOPTION_WEIGHTS.MARKER_LINK +
        sectionCount * stylingWeight +
        ADOPTION_WEIGHTS.VERIFY,
    );

    const adoptionState = JSON.stringify({ v: REPORT_DOWNLOAD_ADOPTION_VERSION, id: downloadId, markers: markers.length });
    if (options.saveState === false) {
      report("save", 1, 1, unitsTotal);
      return true;
    }
    if (state.isNullObject) {
      properties.add(REPORT_DOWNLOAD_ADOPTION_PROPERTY, adoptionState);
    } else {
      state.value = adoptionState;
    }
    await context.sync();
    report("save", 1, 1, unitsTotal);
    return true;
  });
}
